/** @format */
import { css, keyframes } from 'styled-components';

import { mobile, smallTablet } from '@toucan/client/global/application';

import { OmitOnChange, TypeaheadVariant } from './index';

const loadingBarDesktop = keyframes`
    from {
        transform: translateX(-100%);
    }

    to {
        transform: translateX(1000%);
    }
`;

const loadingBarSmallTablet = keyframes`
    from {
        transform: translateX(-100%);
    }

    to {
        transform: translateX(333.33333%);
    }
`;

/**
 * Base styles for the Typeahead component
 *
 * @param {StyledTypeaheadProps} props The Typeahead properties object
 * @param {boolean} props.showBorder If the border underneath the input should be shown
 * @param {boolean} props.isOpen If the component is open
 * */
const baseStyles = ({ showBorder, isOpen }: OmitOnChange) => css`
    position: relative;
    /**
     * Typeahead components displayed underneath
     */
    z-index: ${isOpen ? 10 : 1};

    .input {
        margin-bottom: 0;

        .input-bottom-border {
            border: none;
            height: 2px;
            background: var(--colors-surface-inverse);
            overflow: hidden;

            /* Stops layout jumping when showBorder goes from true to false */
            opacity: ${showBorder ? 1 : 0};

            &::before {
                content: '';
                display: block;
                width: 10%;
                position: absolute;
                left: 0;
                top: 0;
                bottom: 0;
                background: var(--colors-brand-secondary);
                transform: translateX(-100%);

                ${smallTablet(css`
                    width: 30%;
                `)}
            }
        }
    }

    .suggestions-title {
        display: none;
    }

    .no-suggestions,
    .suggestions-text {
        font-size: 0.8rem;
        letter-spacing: 0.16rem;
        color: var(--colors-text-primary);
        text-transform: uppercase;
        font-weight: 500;
    }

    .no-suggestions {
        margin: 0;
    }

    .typeahead-search {
        font: inherit;
        background: transparent;
        border: none;
        display: block;
        width: 100%;
        text-align: left;
        color: var(--colors-text-secondary);
        cursor: pointer;
        padding: 0.9rem 0;
        margin-bottom: 1.5rem;

        strong {
            margin-right: 0.5rem;
            font-weight: 700;
        }

        &:focus {
            outline: none;
        }
    }

    .suggestions-text {
        margin: 0 0 1rem;
    }

    .typeahead__no-suggestions-container,
    .typeahead-items {
        position: absolute;
        top: 100%;
        left: 0;
        background: var(--colors-surface-primary);
        width: 100%;

        .suggestion {
            color: var(--colors-text-secondary);
        }
    }

    .suggestions {
        margin: 0;
        padding: 0;
        overflow: auto;
        max-height: 20.4rem;
    }

    .suggestion {
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 0.9rem 0.5rem;

        &[data-isactive='true'] {
            background: var(--colors-surface-secondary);
        }

        &[data-selected='true'] {
            font-weight: 700;
        }
    }

    .suggestion-close {
        position: absolute;
        top: 1rem;
        right: 1rem;

        .icon svg {
            fill: var(--colors-text-inverse);
        }
    }

    .typeahead-children {
        margin-top: 1.8rem;
        padding-top: var(--layout-spacing-12);
    }
`;

/**
 * Loading styles for the Typeahead component
 *
 * @param {StyledTypeaheadProps} props The Typeahead properties object
 * @param {boolean} props.loading Whether the typeahead is loading suggestions
 * */
const loadingStyles = ({ isLoading }: OmitOnChange) => {
    if (!isLoading) {
        return css``;
    }

    return css`
        .input .input-bottom-border::before {
            animation: ${loadingBarDesktop} 2s infinite linear;

            ${smallTablet(css`
                animation: ${loadingBarSmallTablet} 2s infinite linear;
            `)}
        }
    `;
};

/**
 * Variant styles for the Typeahead component
 *
 * @param {StyledTypeaheadProps} props The Typeahead properties object
 * @param {TypeaheadVariant} props.variant The variant
 * @param {boolean} props.isOpen If the suggestions are open
 * */
const variantStyles = ({ variant, isOpen }: OmitOnChange) => {
    switch (variant) {
        case TypeaheadVariant.SEARCH_BAR:
            return css`
                width: 100%;
                position: static;

                .typeahead__no-suggestions-container,
                .typeahead-items {
                    padding-top: 2rem;
                }

                .input {
                    input,
                    label {
                        padding-left: 4rem;
                        padding-bottom: 1.1rem;
                    }

                    input {
                        margin: 0;
                        padding-top: 1.1rem;
                    }
                }
            `;

        case TypeaheadVariant.INLINE:
        default:
            return css`
                .typeahead__no-suggestions-container,
                .typeahead-items {
                    margin-top: 0.8rem;
                    border-radius: 0.8rem;
                    padding: var(--layout-spacing-24);
                    box-shadow: 0 0.2rem 0.6rem 0 rgba(0, 0, 0, 0.2);
                }

                .typeahead-input-icon {
                    fill: var(--colors-text-secondary);
                }

                .input {
                    .input-bottom-border {
                        height: 1px;
                    }

                    input:focus ~ .input-bottom-border {
                        height: 2px;
                    }
                }

                ${mobile(css`
                    ${isOpen
                        ? css`
                              position: fixed;
                              padding: 0 var(--layout-spacing-12);
                              top: 0;
                              right: 0;
                              bottom: 0;
                              left: 0;
                              background: var(--colors-surface-primary);
                              display: flex;
                              flex-direction: column;
                              z-index: var(
                                  --z-index-mobile-typeahead-suggestions
                              );
                              .typeahead__no-suggestions-container,
                              .typeahead-items {
                                  box-shadow: none;
                                  position: static;
                                  padding-left: 0;
                                  padding-right: 0;
                              }

                              .typeahead-items {
                                  flex-grow: 1;
                                  overflow: auto;
                              }

                              .suggestions {
                                  max-height: none;
                              }

                              .suggestions-title {
                                  display: block;
                                  padding: 1.5rem 1rem 1rem;
                                  background: var(--colors-surface-inverse);
                                  color: var(--colors-text-inverse);
                                  font-size: 1.2rem;
                                  margin: 0 calc(var(--layout-spacing-12) * -1)
                                      var(--layout-spacing-12);
                                  text-align: center;
                              }
                          `
                        : css``}
                `)}
            `;
    }
};

/**
 * Highlight styles for the Typeahead component
 *
 * @param {StyledTypeaheadProps} props The Typeahead properties object
 * @param {number} props.highlightedIndex The highlighted index 

 * */
const highlightStyles = ({ highlightedIndex }: OmitOnChange) => {
    if (highlightedIndex === 0) {
        return css`
            .typeahead-search {
                background-color: var(--colors-surface-secondary);
            }
        `;
    }

    return css``;
};

/**
 * The Typeahead component styles
 *
 * @param {StyledTypeaheadProps} props The Typeahead properties object
 * */
export const styles = (props: OmitOnChange) => css`
    ${baseStyles(props)}
    ${variantStyles(props)}
    ${highlightStyles(props)}

    /* Needs to be at bottom or can break other styles */
    ${loadingStyles(props)}
`;
